import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AreaShell from '../../chrome/AreaShell';
import useOrgs from '../../../Orgs/components/hooks/useOrgs';
import useOrgAccounts from '../../../Orgs/components/hooks/useOrgAccounts';
import useCreateAccount from '../../../Accounts/components/hooks/useCreateAccount';
import { Crumb, Dialog, Field, SectionCard, Select, TextInput } from '../../components/forms';
import { TCard, TEmpty, TSkeleton } from '../../components/table';
import { Frow, useList, useToast } from '../../components/roster';
import { OrgNameDialog, OrgRow, useSwitchIntoOrg } from './AdminOrgsV3';
import './admin-orgs.css';

/*
 * One organization, from Admin: profile, its accounts (classic's
 * Accounts tab, which v2 dropped), and a way into its licenses.
 */

interface Account {
    id: number;
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
}

/* classic's role ids for an org account */
const ROLES = [
    { value: 1, label: 'User', help: 'A standard account in this org.' },
    { value: 2, label: 'Client', help: 'A client account in this org.' },
    { value: 0, label: 'Admin', help: 'Org admin: can change this org’s settings and accounts.' },
];

const accountName = (a: Account) =>
    [a.firstName, a.lastName].filter(Boolean).join(' ') || a.username || a.email || `Account #${a.id}`;

export default function AdminOrgDetailV3() {
    const navigate = useNavigate();
    const params = useParams();
    const orgId = Number(params.orgId);
    const validId = Number.isInteger(orgId) && orgId > 0;
    const { getOrg } = useOrgs();
    const { getAccounts } = useOrgAccounts();
    const { currentId, switchTo } = useSwitchIntoOrg();
    const toast = useToast();

    const [org, setOrg] = useState<OrgRow | null>(null);
    const [orgState, setOrgState] = useState<'loading' | 'ok' | 'failed' | 'missing'>('loading');
    const loadOrg = useCallback(() => {
        if (!validId) {
            setOrgState('missing');
            return;
        }
        setOrgState('loading');
        getOrg(orgId)
            .then((o: OrgRow | null) => {
                setOrg(o ?? null);
                setOrgState(o ? 'ok' : 'missing');
            })
            .catch((e: any) => setOrgState(e?.response?.status === 404 ? 'missing' : 'failed'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);
    useEffect(loadOrg, [loadOrg]);

    const accounts = useList<Account>(() => (validId ? getAccounts(orgId) : null), [orgId]);
    const [renaming, setRenaming] = useState(false);
    const [creating, setCreating] = useState(false);

    const name = org?.name || (validId ? `Organization #${orgId}` : 'Organization');
    const current = org !== null && org.id === currentId;

    return (
        <AreaShell
            area="admin"
            tab="orgs"
            title={orgState === 'ok' ? name : 'Organization'}
            sub={orgState === 'ok' ? `#${orgId}${current ? ' · your current org' : ''}` : undefined}
            actions={
                orgState === 'ok' && org ? (
                    <>
                        <button className="tb-btn" onClick={() => setRenaming(true)}>
                            Rename
                        </button>
                        {!current && (
                            <button className="tb-new" onClick={() => switchTo(org)}>
                                Switch into this org
                            </button>
                        )}
                    </>
                ) : undefined
            }
        >
            <Crumb label="All organizations" onClick={() => navigate('/admin')} />
            {orgState === 'loading' && (
                <TCard>
                    <TSkeleton rows={3} />
                </TCard>
            )}
            {orgState === 'failed' && (
                <TCard>
                    <TEmpty message="Couldn’t load this organization." actionLabel="Retry" onAction={loadOrg} />
                </TCard>
            )}
            {orgState === 'missing' && (
                <TCard>
                    <TEmpty message="That organization doesn’t exist." actionLabel="All organizations" onAction={() => navigate('/admin')} />
                </TCard>
            )}
            {orgState === 'ok' && org && (
                <div className="dets">
                    <div className="stack">
                        <SectionCard title="Organization">
                            <Frow label="Name">{name}</Frow>
                            <Frow label="ID">
                                <span className="adm-mono">#{org.id}</span>
                            </Frow>
                            <p className="pg-sub adm-note">
                                {current
                                    ? 'This is your current org — its profile, white-label, and templates are under Organization.'
                                    : 'To edit its profile, white-label, or templates, switch into it. That changes your current org in this browser.'}
                            </p>
                        </SectionCard>

                        <SectionCard
                            title="Accounts"
                            note={accounts.rows ? String(accounts.rows.length) : undefined}
                            action={
                                <button type="button" className="tb-btn" onClick={() => setCreating(true)}>
                                    Create account
                                </button>
                            }
                        >
                            {accounts.failed ? (
                                <>
                                    <p className="quiet-empty" style={{ marginBottom: 10 }}>Couldn’t load accounts.</p>
                                    <button type="button" className="tb-btn" onClick={accounts.reload}>
                                        Retry
                                    </button>
                                </>
                            ) : accounts.rows === null ? (
                                <p className="quiet-empty">Loading…</p>
                            ) : accounts.rows.length === 0 ? (
                                <p className="quiet-empty">No accounts in this org yet.</p>
                            ) : (
                                accounts.rows.map((a) => (
                                    <div className="trow2" key={a.id}>
                                        <span style={{ minWidth: 0, flex: 1 }}>
                                            <span className="nm2">{accountName(a)}</span>
                                            <br />
                                            <span className="sub2">{a.email || '—'}</span>
                                        </span>
                                        {a.username && <span className="right adm-mono">{a.username}</span>}
                                    </div>
                                ))
                            )}
                        </SectionCard>

                        <SectionCard title="Licenses">
                            <p className="pg-sub" style={{ margin: '0 0 12px' }}>
                                Issue and expire this org’s licenses on the Licenses tab.
                            </p>
                            <button type="button" className="tb-btn" onClick={() => navigate(`/admin/licenses?org=${org.id}`)}>
                                View {name}’s licenses
                            </button>
                        </SectionCard>
                    </div>
                </div>
            )}

            {renaming && org && (
                <OrgNameDialog
                    open
                    row={org}
                    onClose={() => setRenaming(false)}
                    onSaved={(next) => {
                        setOrg({ ...org, name: next });
                        toast.show(`Renamed to ${next}.`);
                    }}
                />
            )}
            {creating && org && (
                <CreateAccountDialog
                    org={org}
                    onClose={() => setCreating(false)}
                    onCreated={(label) => {
                        toast.show(`Created ${label} in ${name}.`);
                        accounts.reload();
                    }}
                />
            )}
            {toast.node}
        </AreaShell>
    );
}

/** Classic's "Add an Account" form, as a dialog. Same body keys as classic. */
function CreateAccountDialog({
    org,
    onClose,
    onCreated,
}: {
    org: OrgRow;
    onClose: () => void;
    onCreated: (label: string) => void;
}) {
    const { createAccount } = useCreateAccount();
    const [f, setF] = useState({ username: '', email: '', firstName: '', lastName: '', password: '', role: 1 });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [touched, setTouched] = useState(false);
    const inFlight = useRef(false);
    const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setF((d) => ({ ...d, [k]: k === 'role' ? Number(e.target.value) : e.target.value }));

    const emailOk = /^\S+@\S+\.\S+$/.test(f.email.trim());
    const missing = {
        username: !f.username.trim(),
        email: !emailOk,
        password: f.password.length === 0,
    };
    const ready = !missing.username && !missing.email && !missing.password;
    const role = ROLES.find((r) => r.value === f.role) ?? ROLES[0];

    const submit = async () => {
        setTouched(true);
        if (!ready || inFlight.current) return;
        inFlight.current = true;
        setSaving(true);
        setError('');
        const response = await createAccount({
            firstName: f.firstName.trim(),
            lastName: f.lastName.trim(),
            username: f.username.trim(),
            email: f.email.trim(),
            password: f.password,
            orgId: org.id,
            role: f.role,
        });
        inFlight.current = false;
        setSaving(false);
        if (response?.data?.id) {
            onCreated([f.firstName.trim(), f.lastName.trim()].filter(Boolean).join(' ') || f.username.trim());
            onClose();
        } else {
            setError(response?.message || 'Couldn’t create the account.');
        }
    };

    return (
        <Dialog
            open
            title={`Create an account in ${org.name}`}
            onClose={() => !saving && onClose()}
            onSubmit={submit}
            footer={
                <>
                    <button type="button" className="tb-btn" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="tb-new" disabled={saving || (touched && !ready)}>
                        {saving ? 'Creating…' : `Create ${role.label.toLowerCase()} account`}
                    </button>
                </>
            }
        >
            <p className="adm-warn">
                This adds a new sign-in to <b>{org.name}</b> — not your current org — with the password you set
                here. Share the password with them privately.
            </p>
            <Field label="Username" error={touched && missing.username ? 'Enter a username.' : undefined}>
                <TextInput autoFocus value={f.username} onChange={set('username')} />
            </Field>
            <Field label="Email" error={touched && missing.email ? 'Enter a valid email address.' : undefined}>
                <TextInput type="email" inputMode="email" value={f.email} onChange={set('email')} />
            </Field>
            <div className="two">
                <Field label="First name">
                    <TextInput value={f.firstName} onChange={set('firstName')} />
                </Field>
                <Field label="Last name">
                    <TextInput value={f.lastName} onChange={set('lastName')} />
                </Field>
            </div>
            <Field
                label="Password"
                help="They sign in with this password."
                error={touched && missing.password ? 'Set a password.' : undefined}
            >
                <TextInput type="password" autoComplete="new-password" value={f.password} onChange={set('password')} />
            </Field>
            <Field label="Role" help={role.help}>
                <Select value={String(f.role)} onChange={set('role')}>
                    {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                            {r.label}
                        </option>
                    ))}
                </Select>
            </Field>
            {error && (
                <p className="field-e" role="alert">
                    {error}
                </p>
            )}
        </Dialog>
    );
}
